/**
 * CC Marketing: Blog CMS List Page
 * Lists all blog posts with status filtering, actions, and template picker.
 * Follows the AdminCampaigns.tsx pattern.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, Clock, Globe, Archive, Search, MoreVertical,
  Eye, Edit2, Send, CalendarClock, ArchiveRestore, Key, Trash2,
  Copy, Check, Users, Upload, X,
} from 'lucide-react';
import { AdminBlogService, BlogPost, BlogTemplate, AgentApiKey, BlogAuthor } from '../services/adminBlogService';

type StatusFilter = 'all' | 'draft' | 'scheduled' | 'published' | 'archived';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
  scheduled: { label: 'Scheduled', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  published: { label: 'Published', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  archived: { label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

export const AdminBlogCMS: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templates, setTemplates] = useState<BlogTemplate[]>([]);

  // Agent keys panel
  const [showAgentKeys, setShowAgentKeys] = useState(false);
  const [agentKeys, setAgentKeys] = useState<AgentApiKey[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Authors panel
  const [showAuthors, setShowAuthors] = useState(false);
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [newAuthorName, setNewAuthorName] = useState('');
  const [newAuthorBio, setNewAuthorBio] = useState('');
  const [authorSaving, setAuthorSaving] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<BlogAuthor | null>(null);
  const [editAuthorName, setEditAuthorName] = useState('');
  const [editAuthorBio, setEditAuthorBio] = useState('');
  const [avatarUploading, setAvatarUploading] = useState<string | null>(null); // authorId being uploaded

  // Action menu
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup copy timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AdminBlogService.listPosts({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setPosts(result.posts);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load posts:', err);
      setError('Failed to load blog posts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleNewPost = async (templateId?: string) => {
    try {
      const post = await AdminBlogService.createPost({
        title: 'Untitled Post',
        template_id: templateId,
      });
      navigate(`/admin/marketing/blog/${post.id}`);
    } catch (err) {
      console.error('Failed to create post:', err);
    }
  };

  const handleOpenTemplatePicker = async () => {
    if (templates.length === 0) {
      try {
        const t = await AdminBlogService.listTemplates();
        setTemplates(t);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    }
    setShowTemplatePicker(true);
  };

  const handleAction = async (postId: string, action: string) => {
    setActiveMenu(null);
    try {
      switch (action) {
        case 'publish':
          await AdminBlogService.publishPost(postId);
          break;
        case 'unpublish':
          await AdminBlogService.unpublishPost(postId);
          break;
        case 'archive':
          await AdminBlogService.archivePost(postId);
          break;
        case 'delete':
          if (!window.confirm('Permanently delete this post? This cannot be undone.')) return;
          await AdminBlogService.deletePost(postId);
          break;
      }
      fetchPosts();
    } catch (err) {
      console.error(`Failed to ${action} post:`, err);
    }
  };

  const handleCreateAgentKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const result = await AdminBlogService.createAgentKey({ name: newKeyName.trim() });
      setCreatedKey(result.api_key);
      setNewKeyName('');
      // Refresh keys
      const keys = await AdminBlogService.listAgentKeys();
      setAgentKeys(keys);
    } catch (err) {
      console.error('Failed to create agent key:', err);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    try {
      await AdminBlogService.revokeAgentKey(keyId);
      const keys = await AdminBlogService.listAgentKeys();
      setAgentKeys(keys);
    } catch (err) {
      console.error('Failed to revoke key:', err);
    }
  };

  const handleOpenAgentKeys = async () => {
    if (agentKeys.length === 0) {
      try {
        const keys = await AdminBlogService.listAgentKeys();
        setAgentKeys(keys);
      } catch (err) {
        console.error('Failed to load agent keys:', err);
      }
    }
    setShowAgentKeys(true);
  };

  const handleOpenAuthors = async () => {
    try {
      const list = await AdminBlogService.listAuthors();
      setAuthors(list);
    } catch (err) {
      console.error('Failed to load authors:', err);
    }
    setShowAuthors(true);
  };

  const handleCreateAuthor = async () => {
    if (!newAuthorName.trim()) return;
    setAuthorSaving(true);
    try {
      const author = await AdminBlogService.createAuthor({ name: newAuthorName.trim(), bio: newAuthorBio.trim() || undefined });
      setAuthors(prev => [...prev, author]);
      setNewAuthorName('');
      setNewAuthorBio('');
    } catch (err) {
      console.error('Failed to create author:', err);
    } finally {
      setAuthorSaving(false);
    }
  };

  const handleDeleteAuthor = async (authorId: string) => {
    if (!window.confirm('Delete this author? Posts will keep their author name but lose the profile.')) return;
    try {
      await AdminBlogService.deleteAuthor(authorId);
      setAuthors(prev => prev.filter(a => a.id !== authorId));
    } catch (err) {
      console.error('Failed to delete author:', err);
    }
  };

  const handleSaveEditAuthor = async () => {
    if (!editingAuthor) return;
    setAuthorSaving(true);
    try {
      const updated = await AdminBlogService.updateAuthor(editingAuthor.id, {
        name: editAuthorName.trim(),
        bio: editAuthorBio.trim() || undefined,
      });
      setAuthors(prev => prev.map(a => a.id === updated.id ? updated : a));
      setEditingAuthor(null);
    } catch (err) {
      console.error('Failed to update author:', err);
    } finally {
      setAuthorSaving(false);
    }
  };

  const handleAvatarUpload = async (authorId: string, file: File) => {
    setAvatarUploading(authorId);
    try {
      const updated = await AdminBlogService.uploadAuthorAvatar(authorId, file);
      setAuthors(prev => prev.map(a => a.id === updated.id ? updated : a));
    } catch (err) {
      console.error('Failed to upload avatar:', err);
    } finally {
      setAvatarUploading(null);
    }
  };

  const filteredPosts = searchQuery
    ? posts.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.slug.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#0B1614', fontFamily: "'League Spartan', sans-serif" }}>Blog CMS</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            Create, edit, and publish blog content for coachcedar.com
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleOpenAuthors}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              fontSize: '13px', color: '#374151', background: 'white',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
            }}
          >
            <Users size={14} /> Authors
          </button>
          <button
            onClick={handleOpenAgentKeys}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              fontSize: '13px', color: '#374151', background: 'white',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
            }}
          >
            <Key size={14} /> Agent Keys
          </button>
          <button
            onClick={handleOpenTemplatePicker}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
              fontSize: '13px', fontWeight: 600, color: 'white', background: '#1F6C4F',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
            }}
          >
            <Plus size={14} /> New Post
          </button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '0' }}>
        {(['all', 'draft', 'scheduled', 'published', 'archived'] as StatusFilter[]).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: statusFilter === s ? 600 : 400,
              color: statusFilter === s ? '#1F6C4F' : '#6b7280',
              borderBottom: statusFilter === s ? '2px solid #1F6C4F' : '2px solid transparent',
              background: 'none', border: 'none', cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <Search size={16} style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }} />
        <input
          type="text"
          placeholder="Search posts..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px 8px 36px', fontSize: '13px',
            border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none',
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 16px', marginBottom: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={fetchPosts} style={{ padding: '4px 12px', fontSize: '12px', color: '#dc2626', background: 'white', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Post List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>Loading...</div>
      ) : filteredPosts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p>No posts found. Create your first post to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredPosts.map(post => {
            const status = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;
            return (
              <div
                key={post.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '16px',
                  padding: '16px', background: 'white',
                  borderRadius: '14px', cursor: 'pointer',
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
                }}
                onClick={() => navigate(`/admin/marketing/blog/${post.id}`)}
              >
                {/* Hero thumbnail */}
                <div style={{
                  width: '64px', height: '64px', borderRadius: '6px', overflow: 'hidden',
                  background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {post.hero_image_url ? (
                    <img src={post.hero_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FileText size={24} style={{ color: '#d1d5db' }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {post.title}
                    </h3>
                    <span style={{
                      fontSize: '11px', fontWeight: 500, padding: '2px 8px',
                      borderRadius: '4px', border: '1px solid',
                    }} className={`${status.color} ${status.bg}`}>
                      {status.label}
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.excerpt || 'No excerpt'}
                  </p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '11px', color: '#9ca3af' }}>
                    <span>{post.author}</span>
                    <span>{post.category}</span>
                    <span>
                      {post.status === 'published' ? `Published ${formatDate(post.published_at)}` :
                       post.status === 'scheduled' ? `Scheduled ${formatDate(post.publish_at)}` :
                       `Created ${formatDate(post.created_at)}`}
                    </span>
                    {post.created_by?.startsWith('agent:') && (
                      <span style={{ color: '#7c3aed', fontWeight: 500 }}>Agent Draft</span>
                    )}
                  </div>
                </div>

                {/* Actions menu */}
                <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setActiveMenu(activeMenu === post.id ? null : post.id)}
                    style={{
                      padding: '6px', background: 'none', border: 'none', cursor: 'pointer',
                      borderRadius: '4px', color: '#6b7280',
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {activeMenu === post.id && (
                    <div style={{
                      position: 'absolute', right: 0, top: '100%', zIndex: 20,
                      background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: '160px', padding: '4px',
                    }}>
                      <button
                        onClick={() => { setActiveMenu(null); navigate(`/admin/marketing/blog/${post.id}`); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#374151' }}
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                      {(post.status === 'draft' || post.status === 'scheduled') && (
                        <button
                          onClick={() => handleAction(post.id, 'publish')}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#059669' }}
                        >
                          <Send size={14} /> Publish Now
                        </button>
                      )}
                      {post.status === 'published' && (
                        <button
                          onClick={() => handleAction(post.id, 'unpublish')}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#d97706' }}
                        >
                          <ArchiveRestore size={14} /> Unpublish
                        </button>
                      )}
                      {post.status !== 'archived' && (
                        <button
                          onClick={() => handleAction(post.id, 'archive')}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#6b7280' }}
                        >
                          <Archive size={14} /> Archive
                        </button>
                      )}
                      <button
                        onClick={() => handleAction(post.id, 'delete')}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#dc2626' }}
                      >
                        <Trash2 size={14} /> Delete permanently
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowTemplatePicker(false)}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>New Blog Post</h2>

            <button
              onClick={() => { setShowTemplatePicker(false); handleNewPost(); }}
              style={{
                width: '100%', padding: '12px 16px', marginBottom: '8px', textAlign: 'left',
                border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb',
                cursor: 'pointer', fontSize: '14px',
              }}
            >
              <strong>Blank Post</strong>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Start from scratch</div>
            </button>

            {templates.map(t => (
              <button
                key={t.id}
                onClick={() => { setShowTemplatePicker(false); handleNewPost(t.id); }}
                style={{
                  width: '100%', padding: '12px 16px', marginBottom: '8px', textAlign: 'left',
                  border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white',
                  cursor: 'pointer', fontSize: '14px',
                }}
              >
                <strong>{t.name}</strong>
                {t.category && <span style={{ fontSize: '11px', color: '#1F6C4F', marginLeft: '8px' }}>{t.category}</span>}
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{t.description}</div>
              </button>
            ))}

            <button
              onClick={() => setShowTemplatePicker(false)}
              style={{ marginTop: '8px', padding: '8px 16px', fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Authors Modal */}
      {showAuthors && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => { setShowAuthors(false); setEditingAuthor(null); }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '100%', maxHeight: '85vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Authors</h2>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Author profiles appear on published blog posts.</p>
              </div>
              <button onClick={() => { setShowAuthors(false); setEditingAuthor(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                <X size={18} />
              </button>
            </div>

            {/* Create new author */}
            <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>New Author</h3>
              <input
                type="text"
                placeholder="Full name"
                value={newAuthorName}
                onChange={e => setNewAuthorName(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box' }}
              />
              <textarea
                placeholder="Short bio (optional)"
                value={newAuthorBio}
                onChange={e => setNewAuthorBio(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical', marginBottom: '10px', boxSizing: 'border-box' }}
              />
              <button
                onClick={handleCreateAuthor}
                disabled={!newAuthorName.trim() || authorSaving}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                  color: 'white', background: newAuthorName.trim() ? '#1F6C4F' : '#9ca3af',
                  border: 'none', borderRadius: '6px', cursor: newAuthorName.trim() ? 'pointer' : 'default',
                }}
              >
                {authorSaving ? 'Creating...' : '+ Create Author'}
              </button>
            </div>

            {/* Author list */}
            {authors.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '16px' }}>No authors yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {authors.map(a => (
                  <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    {editingAuthor?.id === a.id ? (
                      /* Edit form */
                      <div style={{ padding: '14px' }}>
                        <input
                          type="text"
                          value={editAuthorName}
                          onChange={e => setEditAuthorName(e.target.value)}
                          style={{ width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box' }}
                        />
                        <textarea
                          value={editAuthorBio}
                          onChange={e => setEditAuthorBio(e.target.value)}
                          rows={2}
                          style={{ width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical', marginBottom: '10px', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleSaveEditAuthor}
                            disabled={!editAuthorName.trim() || authorSaving}
                            style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: 'white', background: '#1F6C4F', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                          >
                            {authorSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingAuthor(null)}
                            style={{ padding: '6px 12px', fontSize: '12px', color: '#6b7280', background: 'none', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display row */
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px' }}>
                        {/* Avatar */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <label style={{ cursor: 'pointer', display: 'block' }} title="Upload profile photo">
                            <div style={{
                              width: '48px', height: '48px', borderRadius: '50%',
                              overflow: 'hidden', background: '#e8f2ed',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '16px', fontWeight: 700, color: '#1F6C4F',
                            }}>
                              {a.avatar_url ? (
                                <img src={a.avatar_url} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                avatarUploading === a.id ? '...' : a.name.charAt(0).toUpperCase()
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) handleAvatarUpload(a.id, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <div style={{
                            position: 'absolute', bottom: 0, right: 0,
                            width: '16px', height: '16px', borderRadius: '50%',
                            background: '#1F6C4F', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none',
                          }}>
                            <Upload size={9} style={{ color: 'white' }} />
                          </div>
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{a.name}</div>
                          {a.bio && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.bio}</div>}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button
                            onClick={() => { setEditingAuthor(a); setEditAuthorName(a.name); setEditAuthorBio(a.bio || ''); }}
                            style={{ padding: '5px 10px', fontSize: '12px', color: '#374151', background: 'none', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAuthor(a.id)}
                            style={{ padding: '5px 10px', fontSize: '12px', color: '#dc2626', background: 'none', border: '1px solid #fecaca', borderRadius: '5px', cursor: 'pointer' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => { setShowAuthors(false); setEditingAuthor(null); }}
              style={{ marginTop: '16px', padding: '8px 16px', fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Agent Keys Modal */}
      {showAgentKeys && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => { setShowAgentKeys(false); setCreatedKey(null); }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Agent API Keys</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Keys allow agents to create blog drafts via <code>POST /api/agent/blog/drafts</code> with an <code>X-API-Key</code> header.
            </p>

            {/* Create new key */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Key name (e.g. Blog Writer Agent)"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              <button
                onClick={handleCreateAgentKey}
                disabled={!newKeyName.trim()}
                style={{
                  padding: '8px 14px', fontSize: '13px', fontWeight: 600,
                  color: 'white', background: newKeyName.trim() ? '#1F6C4F' : '#9ca3af',
                  border: 'none', borderRadius: '6px', cursor: newKeyName.trim() ? 'pointer' : 'default',
                }}
              >
                Generate
              </button>
            </div>

            {/* Newly created key */}
            {createdKey && (
              <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#166534', marginBottom: '6px' }}>
                  Save this key — it will not be shown again:
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code style={{ flex: 1, fontSize: '12px', padding: '6px 8px', background: 'white', borderRadius: '4px', wordBreak: 'break-all' }}>
                    {createdKey}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(createdKey); setCopiedKey(true); if (copyTimerRef.current) clearTimeout(copyTimerRef.current); copyTimerRef.current = setTimeout(() => setCopiedKey(false), 2000); }}
                    style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {copiedKey ? <Check size={14} style={{ color: '#059669' }} /> : <Copy size={14} style={{ color: '#6b7280' }} />}
                  </button>
                </div>
              </div>
            )}

            {/* Existing keys */}
            {agentKeys.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '16px' }}>No agent keys created yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {agentKeys.map(k => (
                  <div key={k.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px',
                    opacity: k.is_active ? 1 : 0.5,
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{k.name}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {k.key_prefix}... · {k.scopes.join(', ')} · {k.is_active ? 'Active' : 'Revoked'}
                        {k.last_used_at && ` · Last used ${formatDate(k.last_used_at)}`}
                      </div>
                    </div>
                    {k.is_active && (
                      <button
                        onClick={() => handleRevokeKey(k.id)}
                        style={{ padding: '4px 10px', fontSize: '11px', color: '#dc2626', background: 'none', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => { setShowAgentKeys(false); setCreatedKey(null); }}
              style={{ marginTop: '16px', padding: '8px 16px', fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
